import React from 'react';
import { QueryProgress } from '@/lib/trustHub/v1/types';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, Loader2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface ProgressTrackerProps {
  progress: QueryProgress[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  progress,
  isCollapsed,
  onToggleCollapse,
}) => {
  // Group progress by provider
  const groupedProgress = progress.reduce((acc, item) => {
    const provider = item.provider || 'unknown';
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(item);
    return acc;
  }, {} as Record<string, QueryProgress[]>);

  const providers = Object.keys(groupedProgress);

  const getStatusIcon = (status: QueryProgress['status']) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold">Progress</h3>
          <div className="flex items-center gap-3 text-sm flex-wrap">
            {providers.map(provider => {
              const providerItems = groupedProgress[provider];
              const completed = providerItems.filter(p => p.status === 'done').length;
              const total = providerItems.length;
              const errors = providerItems.filter(p => p.status === 'error').length;
              const processing = providerItems.filter(p => p.status === 'processing').length;

              return (
                <div key={provider} className="flex items-center gap-2">
                  <span className="font-medium capitalize">{provider}:</span>
                  <span className="text-muted-foreground">
                    {completed}/{total}
                  </span>
                  {processing > 0 && (
                    <span className="text-blue-600">({processing} processing)</span>
                  )}
                  {errors > 0 && (
                    <span className="text-red-600">({errors} errors)</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
        >
          {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </Button>
      </div>

      {!isCollapsed && (
        <div className="space-y-4 max-h-[300px] overflow-y-auto">
          {providers.map(provider => {
            const providerItems = groupedProgress[provider];
            const completed = providerItems.filter(p => p.status === 'done').length;
            const total = providerItems.length;

            return (
              <div key={provider} className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold capitalize">{provider}</h4>
                  <span className="text-xs text-muted-foreground">
                    {completed}/{total}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mb-2">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${(completed / total) * 100}%` }}
                  />
                </div>
                <div className="space-y-1 pl-2">
                  {providerItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-1.5 rounded hover:bg-muted/50 transition-colors"
                    >
                      {getStatusIcon(item.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{item.query}</p>
                        {item.error && (
                          <div className="mt-1 p-2 bg-destructive/10 rounded border border-destructive/20">
                            <p className="text-xs font-semibold text-destructive mb-1">Error Details:</p>
                            <pre className="text-xs text-destructive whitespace-pre-wrap break-words font-mono">
                              {typeof item.error === 'string' 
                                ? item.error 
                                : JSON.stringify(item.error, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
